/**
 * Unified Product Search Component
 *
 * Reusable autocomplete with:
 * - Debounced search (300ms) against GET /products?search=...
 * - Multi-select with checkboxes and "Select All / Deselect All"
 * - Selected items rendered as removable chips
 * - Single-select mode via `singleSelect` prop
 *
 * Used across: Cannibalization, Manual Override, Report Analytics, etc.
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Box,
  Typography,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon, Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';

/* ─── Types ─── */
export interface ProductOption {
  product_id: string;
  product_name: string;
  sku: string;
  brand?: string;
  category?: string;
  lifecycle_stage?: string;
}

export interface UnifiedProductSearchProps {
  /** Currently-selected products (controlled) */
  value: ProductOption[];
  /** Called when the selection changes */
  onChange: (products: ProductOption[]) => void;
  /** Field label */
  label?: string;
  /** Placeholder when empty */
  placeholder?: string;
  /** Maximum number of selectable items (0 = unlimited) */
  maxSelections?: number;
  /** Restrict results to these brands */
  brandFilter?: string[];
  /** Restrict results to these categories */
  categoryFilter?: string[];
  /** Single-select mode (no checkboxes, one item at a time) */
  singleSelect?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: Record<string, unknown>;
}

/* ─── Helpers ─── */
const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SELECT_ALL_SENTINEL: ProductOption = {
  product_id: '__select_all__',
  product_name: 'Select All / Deselect All',
  sku: '',
};

/* ─── Component ─── */
const UnifiedProductSearch: React.FC<UnifiedProductSearchProps> = ({
  value,
  onChange,
  label = 'Search Products',
  placeholder = 'Type product code or name…',
  maxSelections = 0,
  brandFilter,
  categoryFilter,
  singleSelect = false,
  disabled = false,
  size = 'small',
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Debounced search — 300ms */
  const handleInputChange = useCallback((_: unknown, newValue: string) => {
    setInputValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(newValue), 300);
  }, []);

  /* Query products from API */
  const { data: searchResults = [], isFetching } = useQuery<ProductOption[]>({
    queryKey: ['product-search', debouncedSearch, brandFilter, categoryFilter],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 50 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (brandFilter && brandFilter.length > 0) params.brands = brandFilter.join(',');
      if (categoryFilter && categoryFilter.length > 0) params.categories = categoryFilter.join(',');
      const res = await api.getProducts(params);
      const items = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      return items.map((p: any) => ({
        product_id: p.product_id,
        product_name: p.product_name,
        sku: p.sku || '',
        brand: p.brand,
        category: p.category,
        lifecycle_stage: p.lifecycle_stage,
      }));
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  /* Options: prepend "Select All" toggle when multi-select & results exist */
  const options = useMemo(() => {
    if (singleSelect || searchResults.length === 0) return searchResults;
    return [SELECT_ALL_SENTINEL, ...searchResults];
  }, [searchResults, singleSelect]);

  /* Handle selection change */
  const handleChange = useCallback(
    (_: unknown, newValue: ProductOption | ProductOption[] | null) => {
      if (singleSelect) {
        onChange(newValue ? [newValue as ProductOption] : []);
        return;
      }

      const selected = newValue as ProductOption[];

      /* Detect "Select All" toggle */
      const selectAllClicked = selected.some((p) => p.product_id === '__select_all__');
      if (selectAllClicked) {
        /* If all results are already selected → deselect all; otherwise select all */
        const allSelected = searchResults.every((r) =>
          value.some((v) => v.product_id === r.product_id),
        );
        if (allSelected) {
          /* Remove only the items that came from current results */
          const resultIds = new Set(searchResults.map((r) => r.product_id));
          onChange(value.filter((v) => !resultIds.has(v.product_id)));
        } else {
          /* Merge current selection with all results (no duplicates) */
          const existing = new Set(value.map((v) => v.product_id));
          const merged = [...value, ...searchResults.filter((r) => !existing.has(r.product_id))];
          if (maxSelections > 0) {
            onChange(merged.slice(0, maxSelections));
          } else {
            onChange(merged);
          }
        }
        return;
      }

      if (maxSelections > 0 && selected.length > maxSelections) return;
      onChange(selected);
    },
    [onChange, singleSelect, searchResults, value, maxSelections],
  );

  /* Determine if an option is selected */
  const isOptionSelected = useCallback(
    (option: ProductOption) => value.some((v) => v.product_id === option.product_id),
    [value],
  );

  /* ─── Render ─── */
  return (
    <Autocomplete
      /* Core behaviour */
      multiple={!singleSelect}
      disableCloseOnSelect={!singleSelect}
      options={options}
      value={singleSelect ? (value[0] ?? null) : value}
      onChange={handleChange as any}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disabled={disabled}
      size={size}
      sx={sx}
      /* Matching */
      getOptionLabel={(opt: ProductOption) =>
        opt.product_id === '__select_all__'
          ? opt.product_name
          : `${opt.sku} — ${opt.product_name}`
      }
      isOptionEqualToValue={(opt, val) => opt.product_id === val.product_id}
      filterOptions={(x) => x} /* server-side filtering only */
      loading={isFetching}
      /* Rendering */
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ color: 'var(--color-text-muted)', fontSize: 18, ml: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...restProps } = props as any;
        if (option.product_id === '__select_all__') {
          const allSelected = searchResults.every((r) =>
            value.some((v) => v.product_id === r.product_id),
          );
          return (
            <React.Fragment key="__select_all__">
              <ListItem {...restProps} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={allSelected && searchResults.length > 0}
                    indeterminate={!allSelected && value.length > 0 && searchResults.some((r) => value.some((v) => v.product_id === r.product_id))}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>
                      {allSelected ? 'Deselect All' : 'Select All'} ({searchResults.length})
                    </Typography>
                  }
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          );
        }

        return (
          <ListItem {...restProps} key={option.product_id} dense>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected || isOptionSelected(option)}
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography
                    sx={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, minWidth: 100 }}
                  >
                    {option.sku}
                  </Typography>
                  <Typography sx={{ fontSize: '12px' }}>{option.product_name}</Typography>
                </Box>
              }
              secondary={
                option.brand || option.category
                  ? `${option.brand || ''}${option.brand && option.category ? ' · ' : ''}${option.category || ''}`
                  : undefined
              }
              secondaryTypographyProps={{ sx: { fontSize: '11px' } }}
            />
          </ListItem>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.sku || option.product_name}
              size="small"
              {...tagProps}
              sx={{
                fontSize: '11px',
                height: 24,
                bgcolor: 'var(--color-accent-bg, #f5f0e8)',
                color: 'var(--color-accent, #8b7355)',
                '& .MuiChip-deleteIcon': {
                  color: 'var(--color-accent, #8b7355)',
                  fontSize: 14,
                },
              }}
            />
          );
        })
      }
      noOptionsText={
        inputValue.length > 0
          ? 'No products found'
          : 'Start typing to search products…'
      }
      /* List props */
      ListboxProps={{ sx: { maxHeight: 320 } }}
    />
  );
};

export default UnifiedProductSearch;
